import React from 'react';
import { MCPServer, MCPCapability } from '@/shared/types';
import { DynamicToolForm } from './DynamicToolForm';

interface ServerDetailsPanelProps {
  server: MCPServer;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  filteredCapabilities: MCPCapability[];
  executingTool: any;
  onStartToolExecution: (tool: any) => void;
  onExecuteTool: (data: any) => void;
  onCancelToolExecution: () => void;
  executionResult: any;
  loading: boolean;
}

export const ServerDetailsPanel: React.FC<ServerDetailsPanelProps> = ({
  server,
  searchQuery,
  onSearchChange,
  filteredCapabilities,
  executingTool,
  onStartToolExecution,
  onExecuteTool,
  onCancelToolExecution,
  executionResult,
  loading,
}) => {
  return (
    <div className="flex flex-1 overflow-hidden h-full">
      {/* Left Panel - Server Details & Tools */}
      <div className="w-1/2 border-r border-gray-700 flex flex-col h-full">
        <div className="p-6 border-b border-gray-700 flex-shrink-0">
          <h4 className="text-lg font-semibold text-white mb-4">Server Details</h4>
          <div className="bg-gray-800/50 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-400">URL:</span>
              <code className="text-sm text-blue-300 bg-gray-900 px-2 py-1 rounded">{server?.url}</code>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-400">Status:</span>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                server?.status === 'connected'
                  ? 'bg-green-600 text-green-200'
                  : server?.status === 'error'
                  ? 'bg-red-600 text-red-200'
                  : 'bg-gray-600 text-gray-200'
              }`}>
                {server?.status}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-400">Auth Type:</span>
              <span className="text-sm text-white capitalize">{server?.authType}</span>
            </div>
          </div>
        </div>
        
        <div className="flex-1 overflow-hidden flex flex-col min-h-0">
          <div className="p-6 border-b border-gray-700 flex-shrink-0">
            <h4 className="text-lg font-semibold text-white mb-2">Available Tools</h4>
            <p className="text-sm text-gray-400 mb-4">Click on any tool card to select it for execution</p>
            
            {/* Search input */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search tools..."
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <svg className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto p-6 min-h-0">
            <div className="space-y-3">
              {filteredCapabilities.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-400">
                    {searchQuery ? 'No tools found matching your search' : 'No tools available'}
                  </p>
                </div>
              ) : (
                filteredCapabilities.map((capability, index) => (
                  <div 
                    key={index} 
                    className={`bg-gray-800/50 rounded-xl p-4 border transition-colors cursor-pointer ${
                      executingTool?.name === capability.name 
                        ? 'border-blue-500 bg-blue-900/20' 
                        : 'border-gray-700 hover:border-gray-600'
                    }`}
                    onClick={() => onStartToolExecution(capability)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h5 className="text-base font-semibold text-white mb-1">{capability.name}</h5>
                        <p className="text-sm text-gray-400">{capability.description}</p>
                      </div>
                      <div className="flex items-center gap-2 ml-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          capability.enabled ? 'bg-green-600 text-green-200' : 'bg-gray-600 text-gray-200'
                        }`}>
                          {capability.enabled ? 'Enabled' : 'Disabled'}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onStartToolExecution(capability);
                          }}
                          className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-lg font-medium transition-colors"
                          disabled={!capability.enabled}
                        >
                          Execute
                        </button>
                      </div>
                    </div>
                    {capability.parameters && capability.parameters.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-700">
                        <p className="text-xs text-gray-400 mb-2 font-medium">Parameters:</p>
                        <div className="space-y-1">
                          {capability.parameters.map((param, paramIndex) => (
                            <div key={paramIndex} className="flex items-center gap-2 text-xs">
                              <code className="text-blue-300 bg-gray-900 px-2 py-1 rounded">{param.name}</code>
                              <span className="text-gray-500">({param.type})</span>
                              {param.required && <span className="text-red-400 font-medium">*</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Execution Interface */}
      <div className="w-1/2 flex flex-col h-full">
        <div className="p-6 border-b border-gray-700 flex-shrink-0">
          <h4 className="text-lg font-semibold text-white mb-2">Tool Execution</h4>
          <p className="text-sm text-gray-400">
            {executingTool ? `Executing: ${executingTool.name}` : 'Select a tool to execute'}
          </p>
        </div>
        
        <div className="flex-1 overflow-hidden flex flex-col min-h-0">
          {executingTool ? (
            <div className="flex-1 overflow-y-auto p-6 min-h-0">
              <DynamicToolForm
                tool={executingTool}
                onExecute={onExecuteTool}
                onCancel={onCancelToolExecution}
                executionResult={executionResult}
              />
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center p-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h5 className="text-lg font-medium text-white mb-2">No Tool Selected</h5>
                <p className="text-gray-400">Choose a tool from the left panel to execute it</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
